/**
 * Dünner Client für die JSON-API. Er kennt keine Anwendungslogik: er baut
 * Requests, wertet Statuscodes aus und wirft typisierte Fehler.
 */

import type { RouteAnswer } from '../lib/route'
import type { RouteStep, RouteTunnel } from '../lib/tulipSuggest'
import type { LatLon, TrackFix } from '../lib/types'

/** Ein Dokument in der Fassung des Servers. */
export interface ServerDoc {
  kind: string
  id: string
  body: unknown
  rev: number
  deleted: boolean
  seq: number
  updatedAt: number
}

/** Eine Änderung, wie der Client sie vorschlägt. */
export interface ChangeRequest {
  kind: string
  id: string
  body: unknown
  baseRev: number
  deleted: boolean
}

export interface ApplyResult {
  kind: string
  id: string
  /**
   * Was aus der Änderung geworden ist.
   *
   * - 'applied': geschrieben.
   * - 'conflict': jemand war schneller. doc trägt die Serverfassung, der
   *   Client entscheidet, was daraus wird - beim nächsten Versuch kann es
   *   durchgehen.
   * - 'forbidden': diese Änderung darf dieser Nutzer nicht machen, nämlich
   *   den Tagebuchbeitrag eines anderen ändern oder löschen. doc trägt
   *   ebenfalls die Serverfassung, aber die Absage fällt beim nächsten
   *   Versuch genauso aus. Sie als Konflikt zu behandeln hiesse, sie für
   *   immer zu wiederholen.
   */
  status: 'applied' | 'conflict' | 'forbidden'
  doc: ServerDoc
}

export interface PullResponse {
  changes: ServerDoc[]
  cursor: number
  hasMore: boolean
  /**
   * Auf welche Reise sich diese Antwort bezieht.
   *
   * Auf dem ausdrücklichen Weg ist das die aus dem Pfad, also nichts Neues.
   * Auf dem Altweg ohne Reise ist es die einzige Auskunft darüber, worin der
   * Server gerade geschrieben hätte - genau daran erkennt diese Fassung, zu
   * welcher Reise ein Altbestand gehört (siehe adoptiereAltbestand in db.ts).
   * Ältere Server liefern das Feld nicht.
   */
  tripId?: string
}

export interface PushResponse extends PullResponse {
  results: ApplyResult[]
}

/** Anmeldeparameter, die der Server zur Laufzeit liefert. */
export interface ClientConfig {
  issuer: string
  clientId: string
  scopes: string
  devMode: boolean
  version: string
  /**
   * Liegt auf dem Server ein Kachelarchiv unter /tiles/basemap.pmtiles?
   *
   * Nur der Server weiss das - das Archiv ist eine Datei im Datenverzeichnis
   * und nicht Teil des Deploys. Ohne sie zeigt die App die schematische Karte,
   * statt eine leere Fläche aufzuspannen.
   */
  hasBasemap: boolean
}

export interface Identity {
  sub: string
  email: string
  name: string
  groups: string[]
}

/**
 * Die Rollen einer Mitgliedschaft, aufsteigend.
 *
 * Dieselben Namen wie auf dem Server (store.RoleRead und so weiter): read
 * sieht, modify bearbeitet die Inhalte, admin verwaltet zusätzlich Mitglieder
 * und darf die Reise umbenennen und löschen.
 */
export const ROLLEN = ['read', 'modify', 'admin'] as const

export type Role = (typeof ROLLEN)[number]

/** Ob dieser Wert eine bekannte Rolle ist. Alles andere reicht für nichts. */
export function istRolle(wert: unknown): wert is Role {
  return typeof wert === 'string' && (ROLLEN as readonly string[]).includes(wert)
}

/**
 * Eine Reise, wie der Server sie auflistet - samt der eigenen Rolle darin.
 *
 * Nicht zu verwechseln mit Trip aus lib/types.ts: das ist der Inhalt der Reise
 * (Etappen, Rahmendaten) und liegt als Dokument im Abgleich. Hier steht das
 * Objekt, an dem die Berechtigung hängt.
 */
export interface TripSummary {
  id: string
  name: string
  role: Role
  /** Wer sie angelegt hat, als E-Mail-Adresse. Reine Auskunft. */
  createdBy: string
  createdAt: number
  updatedAt: number
}

/** Ein Mitglied einer Reise. Berechtigt wird über die Adresse, nicht über ein Konto. */
export interface Member {
  email: string
  role: Role
  createdAt: number
  updatedAt: number
}

export interface PhotoUploadResult {
  doc: ServerDoc
  photo: {
    id: string
    stageId: string
    mime: string
    size: number
    sha256: string
    createdAt: number
  }
}

/**
 * Was der Server über eine Strecke sagt.
 *
 * Erweitert RouteAnswer aus lib/route.ts: dort stehen die Felder, die ins
 * Datenmodell wandern, hier die beiden, die nur die Herkunft der Antwort
 * beschreiben und nirgends gespeichert werden.
 */
export interface RouteResponse extends RouteAnswer {
  /**
   * Die Abbiegeanweisungen der Strecke, sofern welche hinterlegt sind.
   *
   * Sie kommen nur, wenn ausdrücklich danach gefragt wurde (mitSteps), und
   * können auch dann fehlen - dann hat der Dienst keine geliefert. Was daraus
   * wird, entscheidet lib/tulipSuggest.ts.
   */
  steps?: RouteStep[]
  /**
   * Die Tunnel auf der Strecke, aus dem Kachelarchiv des Servers.
   *
   * Kommt ebenfalls nur auf Verlangen (mitSteps) und fehlt ganz, wenn kein
   * Archiv hinterlegt ist. Das Fehlen ist eine eigene Auskunft und nicht
   * dasselbe wie eine leere Liste: „nicht nachgesehen“ gegen „nachgesehen und
   * nichts gefunden“. Die Oberfläche sagt beides verschieden.
   */
  tunnels?: {
    /** Anteil der Strecke, für den das Archiv Kacheln hat, 0 bis 1. */
    coverage: number
    found: RouteTunnel[]
  }
  /** Welcher Dienst gerechnet hat, etwa "osrm". */
  provider: string
  /** Kam die Antwort aus dem Zwischenspeicher des Servers? */
  cached: boolean
}

/**
 * Was der Server auf eine Adresssuche antwortet.
 *
 * results ist bewusst unknown: die Treffer sind eine fremde Auskunft und
 * werden geprüft, bevor eine Koordinate daraus in den Bestand geht (placesFrom
 * in lib/geocode.ts). Dieser Client kennt keine Anwendungslogik und soll auch
 * keine Zusage machen, die er nicht prüft.
 */
export interface GeocodeResponse {
  results: unknown
  /** Welcher Dienst gesucht hat, etwa "nominatim". */
  provider: string
  /** Kam die Antwort aus dem Zwischenspeicher des Servers? */
  cached: boolean
}

/**
 * Was der Server auf die Frage nach dem Ort einer Koordinate antwortet.
 *
 * place ist bewusst unknown, aus demselben Grund wie GeocodeResponse.results -
 * und es ist null, wenn der Dienst dort keinen Ort kennt. Das ist eine Antwort
 * und keine Störung: mitten im Meer gibt es keinen.
 */
export interface ReverseResponse {
  place: unknown
  /** Welcher Dienst geantwortet hat, etwa "nominatim". */
  provider: string
  /** Kam die Antwort aus dem Zwischenspeicher des Servers? */
  cached: boolean
}

/**
 * Was der Server über das Wetter entlang einer Fahrt sagt.
 *
 * hours ist bewusst unknown, aus demselben Grund wie GeocodeResponse.results:
 * die Werte sind eine fremde Auskunft und werden geprüft, bevor eine Zahl
 * daraus in den Bestand geht (weatherHoursFrom in lib/weather.ts).
 */
export interface WeatherResponse {
  hours: unknown
  /** Welcher Dienst geantwortet hat, etwa "open-meteo". */
  provider: string
  /**
   * Die Namensnennung, die die Lizenz des Dienstes verlangt.
   *
   * Sie kommt mit den Werten und wird mit ihnen gespeichert: eine Nennung, die
   * nur in einer Ansicht steht, kann beim nächsten Umbau verlorengehen.
   */
  attribution: string
  /** Kam die Antwort aus dem Zwischenspeicher des Servers? */
  cached: boolean
}

/**
 * Was der Server über den Belag einer Strecke sagt.
 *
 * report ist bewusst unknown, aus demselben Grund wie GeocodeResponse.results
 * und WeatherResponse.hours: die Auskunft ist fremd und wird geprüft, bevor
 * eine Zahl daraus in den Bestand geht (surfaceReportFrom in lib/surface.ts).
 * Dieser Client kennt keine Anwendungslogik und macht keine Zusage, die er
 * nicht prüft.
 *
 * Die beiden Zahlen daneben sind der Fortschritt und stehen deshalb ausserhalb:
 * an ihnen entscheidet der Aufrufer, ob er noch einmal fragt, und sie wandern
 * nicht in den Bestand.
 */
export interface SurfaceResponse {
  report: unknown
  /**
   * Wie viele Blöcke der Strecke noch nicht abgefragt sind.
   *
   * Über null heisst: derselbe Aufruf noch einmal, und die Antwort wird
   * vollständiger. Null heisst: mehr ist nicht zu holen - ist die Abdeckung
   * dann niedrig, liegt es nicht am Warten.
   */
  pending: number
  /** Wie viele Blöcke es insgesamt sind. Zusammen mit pending der Fortschritt. */
  blocks: number
  /** Welcher Dienst geantwortet hat, etwa "overpass". */
  provider: string
  /**
   * Die Namensnennung, die die Lizenz der Daten verlangt.
   *
   * Sie kommt mit der Auskunft und wird mit ihr angezeigt: eine Nennung, die
   * nur in einer Ansicht steht, kann beim nächsten Umbau verlorengehen.
   */
  attribution: string
}

/** Fehler mit HTTP-Status, also eine Antwort des Servers. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /**
   * Ein abgelaufenes oder fehlendes Token. Der Aufrufer sollte neu anmelden.
   *
   * Ausdrücklich nur 401. Bis zum Reisemodell zählte 403 mit, weil es nur
   * einen Grund dafür gab: die Anmeldung. Jetzt sagt 403 "du bist Mitglied,
   * deine Rolle reicht dafür nicht" - eine Auskunft über die Rolle und nicht
   * über die Sitzung. Wer daraufhin abmeldet, wirft einen Nutzer aus der App,
   * der nur gerade kein Foto hochladen darf.
   */
  get isAuthProblem(): boolean {
    return this.status === 401
  }

  /** Mitglied, aber die Rolle reicht nicht. */
  get istVerboten(): boolean {
    return this.status === 403
  }

  /**
   * Gibt es für diesen Aufrufer nicht.
   *
   * Bei einer Reise heisst das zweierlei und ist von aussen nicht zu
   * unterscheiden: sie wurde gelöscht, oder die Mitgliedschaft wurde entzogen.
   * Beides ist kein Datenverlust, siehe SyncEngine.tripVerloren.
   */
  get istUnbekannt(): boolean {
    return this.status === 404
  }
}

/** Fehler ohne Antwort: kein Netz, abgebrochene Verbindung, Zeitüberschreitung. */
export class OfflineError extends Error {
  constructor(cause?: unknown) {
    super('Server nicht erreichbar')
    this.name = 'OfflineError'
    this.cause = cause
  }
}

/** Liefert das aktuelle Zugriffstoken oder null, wenn keines vorliegt. */
export type TokenProvider = () => Promise<string | null>

export class ApiClient {
  constructor(
    private readonly getToken: TokenProvider,
    private readonly baseUrl = '',
  ) {}

  /** Die Anmeldeparameter braucht der Client, bevor er ein Token hat. */
  async fetchConfig(signal?: AbortSignal): Promise<ClientConfig> {
    return this.request<ClientConfig>('GET', '/api/v1/config', { auth: false, signal })
  }

  async fetchIdentity(signal?: AbortSignal): Promise<Identity> {
    return this.request<Identity>('GET', '/api/v1/me', { signal })
  }

  // --- Reisen ---------------------------------------------------------------

  /** Die Reisen, in denen der Aufrufer Mitglied ist, samt eigener Rolle. */
  async listTrips(signal?: AbortSignal): Promise<TripSummary[]> {
    const antwort = await this.request<{ trips: TripSummary[] | null }>('GET', '/api/v1/trips', {
      signal,
    })
    return antwort.trips ?? []
  }

  async createTrip(name: string, signal?: AbortSignal): Promise<TripSummary> {
    return this.request<TripSummary>('POST', '/api/v1/trips', { body: { name }, signal })
  }

  async renameTrip(tripId: string, name: string, signal?: AbortSignal): Promise<TripSummary> {
    return this.request<TripSummary>('PATCH', `/api/v1/trips/${encodeURIComponent(tripId)}`, {
      body: { name },
      signal,
    })
  }

  async deleteTrip(tripId: string, signal?: AbortSignal): Promise<void> {
    await this.sendChecked('DELETE', `/api/v1/trips/${encodeURIComponent(tripId)}`, { signal })
  }

  async listMembers(tripId: string, signal?: AbortSignal): Promise<Member[]> {
    const antwort = await this.request<{ members: Member[] | null }>(
      'GET',
      `/api/v1/trips/${encodeURIComponent(tripId)}/members`,
      { signal },
    )
    return antwort.members ?? []
  }

  /** Legt ein Mitglied an oder ändert seine Rolle. */
  async putMember(
    tripId: string,
    email: string,
    role: Role,
    signal?: AbortSignal,
  ): Promise<Member> {
    return this.request<Member>('PUT', memberPath(tripId, email), { body: { role }, signal })
  }

  async removeMember(tripId: string, email: string, signal?: AbortSignal): Promise<void> {
    await this.sendChecked('DELETE', memberPath(tripId, email), { signal })
  }

  // --- Abgleich -------------------------------------------------------------

  async pull(tripId: string, since: number, signal?: AbortSignal): Promise<PullResponse> {
    return this.request<PullResponse>(
      'GET',
      `/api/v1/trips/${encodeURIComponent(tripId)}/sync?since=${since}`,
      { signal },
    )
  }

  async push(
    tripId: string,
    changes: ChangeRequest[],
    since: number,
    signal?: AbortSignal,
  ): Promise<PushResponse> {
    return this.request<PushResponse>('POST', `/api/v1/trips/${encodeURIComponent(tripId)}/sync`, {
      body: { changes, since },
      signal,
    })
  }

  /**
   * Fragt den Altweg, zu welcher Reise ein Bestand ohne Reise-ID gehört.
   *
   * Der einzige Aufruf dieser Fassung auf /api/v1/sync, und er dient nicht dem
   * Abgleich, sondern der Zuordnung: der Server löst dort die älteste
   * Mitgliedschaft auf - genau die Reise also, aus der ein Bestand stammt, der
   * noch ohne Reise-ID auf dem Gerät liegt. Ihn zu raten wäre die eine
   * Vermischung zweier Reisen, die sich hinterher nicht mehr auflösen lässt.
   *
   * Gelesen wird nur das Feld tripId. Was an Änderungen mitkommt, wird
   * verworfen und der Cursor nicht bewegt: dieser Aufruf darf am Bestand nichts
   * ändern. limit=1 hält die Antwort klein, since sorgt dafür, dass im
   * Regelfall ohnehin nichts kommt.
   *
   * @returns die Reise-ID, oder eine leere Zeichenkette wenn der Aufrufer gar
   *   keine Mitgliedschaft hat (oder der Server das Feld noch nicht kennt).
   */
  async legacyTripId(since: number, signal?: AbortSignal): Promise<string> {
    const antwort = await this.request<PullResponse>('GET', `/api/v1/sync?since=${since}&limit=1`, {
      signal,
    })
    return antwort.tripId ?? ''
  }

  /**
   * Lässt eine Strasse von einem Punkt über beliebige Zwischenpunkte zu einem
   * anderen berechnen.
   *
   * via ist die geordnete Liste der Stellen, über die die Strecke führen muss;
   * eine leere Liste ergibt eine Anfrage wie vor der Einführung der
   * Zwischenpunkte. Je Punkt ein eigener Parameter, weil eine Query-Zeichenkette
   * die Reihenfolge gleichnamiger Parameter behält - und die Reihenfolge ist
   * hier die der Fahrt.
   *
   * Die Fahrzeit in der Antwort gilt für einen gewöhnlichen Personenwagen -
   * der Server gibt weiter, was der Dienst sagt, und rechnet nichts hinzu. Was
   * daraus für den Camper wird, entscheidet lib/route.ts und am Ende der
   * Nutzer.
   *
   * mitSteps verlangt zusätzlich die Abbiegeanweisungen - die Grundlage der
   * Tulpenvorschläge. Beim Routendienst kostet das nichts, sie hängen an
   * derselben Berechnung; der Parameter wirkt nur auf den Zwischenspeicher des
   * Servers, wo ein Eintrag ohne Manöver dann als Fehltreffer gilt. Deshalb
   * steht er ausdrücklich und ist nicht immer an: der Sammelaufruf für die
   * Strecken würde sonst jede schon berechnete Etappe noch einmal beim fremden
   * Dienst holen.
   */
  async fetchRoute(
    from: LatLon,
    via: LatLon[],
    to: LatLon,
    signal?: AbortSignal,
    mitSteps = false,
  ): Promise<RouteResponse> {
    const query = [
      `from=${coordParam(from)}`,
      ...via.map((punkt) => `via=${coordParam(punkt)}`),
      `to=${coordParam(to)}`,
      ...(mitSteps ? ['steps=1'] : []),
    ].join('&')
    return this.request<RouteResponse>('GET', `/api/v1/route?${query}`, { signal })
  }

  /**
   * Holt, was über den Belag einer Strecke bekannt ist.
   *
   * Derselbe Zuschnitt wie fetchRoute - Start, Zwischenpunkte, Ziel -, denn es
   * ist dieselbe Strecke; der Server schlägt den Belag an ihren OSM-Knoten
   * nach.
   *
   * Der Aufruf ist teuer und kann unvollständig antworten. Das ist keine
   * Störung, sondern die Bauart: Overpass ist langsam und unzuverlässig, eine
   * lange Etappe zerfällt in mehrere Abfragen, und der Server beantwortet, was
   * in seine Frist passt. Was er geholt hat, legt er ab; "pending" sagt, wie
   * viele Blöcke noch fehlen. Wer alles will, ruft noch einmal - und beim
   * zweiten Mal kostet das Geholte nichts mehr.
   */
  async fetchSurface(
    from: LatLon,
    via: LatLon[],
    to: LatLon,
    signal?: AbortSignal,
  ): Promise<SurfaceResponse> {
    const query = [
      `from=${coordParam(from)}`,
      ...via.map((punkt) => `via=${coordParam(punkt)}`),
      `to=${coordParam(to)}`,
    ].join('&')
    return this.request<SurfaceResponse>('GET', `/api/v1/surface?${query}`, { signal })
  }

  /**
   * Sucht Orte zu einem Suchbegriff.
   *
   * Gerufen wird das nur, wenn jemand ausdrücklich sucht - nicht beim Tippen.
   * Der Server drosselt auf eine Anfrage je Sekunde, weil die Nutzungsordnung
   * des Adressdienstes das verlangt; eine Suche bei jedem Tastendruck wäre
   * genau die Massenabfrage, die dort untersagt ist.
   */
  async searchPlaces(query: string, signal?: AbortSignal): Promise<GeocodeResponse> {
    return this.request<GeocodeResponse>('GET', `/api/v1/geocode?q=${encodeURIComponent(query)}`, {
      signal,
    })
  }

  /**
   * Sucht den Ort zu einer Koordinate.
   *
   * Die Gegenrichtung von searchPlaces, an denselben Dienst und mit derselben
   * Drosselung: eine Anfrage je Sekunde. Gerufen wird das, wenn aus einer
   * Aufzeichnung Etappen entstehen - je Etappe ein Punkt und nicht je
   * Trackpunkt (benoetigteOrte in features/diary/anlegen.ts). Wer eine
   * Aufzeichnung im Sekundentakt Punkt für Punkt benennen wollte, sässe Stunden
   * davor und wäre genau die Massenabfrage, die dort untersagt ist.
   */
  async reversePlace(lat: number, lon: number, signal?: AbortSignal): Promise<ReverseResponse> {
    const query = `lat=${String(lat)}&lon=${String(lon)}`
    return this.request<ReverseResponse>('GET', `/api/v1/reverse?${query}`, { signal })
  }

  /**
   * Holt die Stundenwerte des Wetters entlang einer gefahrenen Strecke.
   *
   * Gefragt wird mit den Punkten der Aufzeichnung - je Punkt ein Parameter
   * „at“ der Form „Breite,Länge,Sekunden“. Ohne Aufzeichnung gibt es keine
   * Punkte, und dann wird gar nicht erst gefragt: der Aufrufer prüft das,
   * dieser Client stellt keine leere Anfrage.
   *
   * Die Zeiten gehen in Sekunden hinaus und nicht in Millisekunden. Der Dienst
   * liefert stündlich, der Server schneidet ohnehin auf die volle Stunde ab -
   * und die kürzere Zahl macht die URL lesbar und den Schlüssel des
   * Zwischenspeichers stabil.
   */
  async fetchWeather(samples: TrackFix[], signal?: AbortSignal): Promise<WeatherResponse> {
    if (samples.length === 0) {
      throw new Error('fetchWeather ohne Punkte: ohne Aufzeichnung gibt es nichts zu fragen')
    }
    const query = samples
      .map((fix) => `at=${coordParam(fix.at)},${String(Math.floor(fix.t / 1000))}`)
      .join('&')
    return this.request<WeatherResponse>('GET', `/api/v1/weather?${query}`, { signal })
  }

  async uploadPhoto(
    tripId: string,
    id: string,
    stageId: string,
    blob: Blob,
    signal?: AbortSignal,
  ): Promise<PhotoUploadResult> {
    const query = stageId ? `?stageId=${encodeURIComponent(stageId)}` : ''
    return this.request<PhotoUploadResult>('PUT', `${photoPath(tripId, id)}${query}`, {
      raw: blob,
      contentType: blob.type || 'image/jpeg',
      signal,
    })
  }

  /** Lädt die Bilddaten eines Fotos herunter. */
  async downloadPhoto(tripId: string, id: string, signal?: AbortSignal): Promise<Blob> {
    const response = await this.sendChecked('GET', photoPath(tripId, id), { signal })
    return response.blob()
  }

  private async request<T>(
    method: string,
    path: string,
    options: {
      body?: unknown
      raw?: Blob
      contentType?: string
      auth?: boolean
      signal?: AbortSignal
    } = {},
  ): Promise<T> {
    const response = await this.sendChecked(method, path, options)
    return (await response.json()) as T
  }

  /**
   * Wie send, wirft aber bei einer Absage des Servers.
   *
   * Für alles, was keinen JSON-Rumpf zurückbringt: ein 204 auf DELETE hat
   * gar keinen, und response.json() scheiterte daran mit einer Meldung über
   * kaputtes JSON, obwohl alles in Ordnung ist.
   */
  private async sendChecked(
    method: string,
    path: string,
    options: {
      body?: unknown
      raw?: Blob
      contentType?: string
      auth?: boolean
      signal?: AbortSignal
    } = {},
  ): Promise<Response> {
    const response = await this.send(method, path, options)
    await throwOnError(response)
    return response
  }

  private async send(
    method: string,
    path: string,
    options: {
      body?: unknown
      raw?: Blob
      contentType?: string
      auth?: boolean
      signal?: AbortSignal
    },
  ): Promise<Response> {
    const headers = new Headers()
    let payload: BodyInit | undefined

    if (options.raw) {
      payload = options.raw
      headers.set('Content-Type', options.contentType ?? 'application/octet-stream')
    } else if (options.body !== undefined) {
      payload = JSON.stringify(options.body)
      headers.set('Content-Type', 'application/json')
    }

    if (options.auth !== false) {
      const token = await this.getToken()
      if (!token) {
        throw new ApiError(401, 'unauthenticated', 'Kein Zugriffstoken vorhanden')
      }
      headers.set('Authorization', `Bearer ${token}`)
    }

    try {
      return await fetch(this.baseUrl + path, {
        method,
        headers,
        body: payload,
        signal: options.signal,
        credentials: 'omit',
      })
    } catch (cause) {
      // fetch wirft nur, wenn die Anfrage gar nicht erst durchkommt.
      if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
      throw new OfflineError(cause)
    }
  }
}

function photoPath(tripId: string, photoId: string): string {
  return `/api/v1/trips/${encodeURIComponent(tripId)}/photos/${encodeURIComponent(photoId)}`
}

/**
 * Der Pfad einer Mitgliedschaft.
 *
 * Die Adresse wird kodiert, das @ wird also zu %40. Der Server packt sie wieder
 * aus (memberEmail in internal/api/trips.go); roh geschickt würde ein Zeichen
 * wie das Pluszeichen unterwegs zur Bedeutung wechseln.
 */
function memberPath(tripId: string, email: string): string {
  return `/api/v1/trips/${encodeURIComponent(tripId)}/members/${encodeURIComponent(email)}`
}

/**
 * Eine Koordinate als Abfrageparameter, "Breite,Länge".
 *
 * Auf fünf Nachkommastellen gekürzt, das sind gut anderthalb Meter und damit
 * weit genauer, als eine Strasse breit ist. Genau diese Stellenzahl ist auch
 * der Schlüssel, unter dem der Server seine Antworten ablegt: was hier
 * gekürzt herausgeht, trifft dort ohne Umweg denselben Eintrag.
 *
 * Der Umweg über Number() nimmt die Nullen am Ende wieder weg - "7.44" statt
 * "7.44000", weil eine URL nicht so tun soll, als wäre sie genauer.
 */
function coordParam([lat, lon]: LatLon): string {
  const kurz = (v: number) => (Number.isFinite(v) ? String(Number(v.toFixed(5))) : '0')
  return `${kurz(lat)},${kurz(lon)}`
}

async function throwOnError(response: Response): Promise<void> {
  if (response.ok) return

  let code = 'http_' + response.status
  let message = response.statusText || 'Anfrage fehlgeschlagen'
  try {
    const body = (await response.json()) as { error?: string; message?: string }
    if (body.error) code = body.error
    if (body.message) message = body.message
  } catch {
    // Antwort ohne JSON-Rumpf: Statuscode und Statustext müssen reichen.
  }
  throw new ApiError(response.status, code, message)
}
